import { useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// ChipsInput — space/enter/comma separated list of tags
function ChipsInput({ value, onChange, placeholder, testid }) {
    const [text, setText] = useState("");
    const list = Array.isArray(value) ? value : [];
    const add = () => {
        const parts = text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
        if (parts.length) onChange([...list, ...parts]);
        setText("");
    };
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    return (
        <div>
            <div className="flex flex-wrap gap-1.5 mb-2">
                {list.map((v, i) => (
                    <span key={i} className="ddi-badge bg-[#0F766E]/10 text-[#0F766E]">
                        {v}
                        <button type="button" onClick={() => remove(i)} className="ml-1 hover:text-[#DC2626]">
                            <X size={10} />
                        </button>
                    </span>
                ))}
            </div>
            <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        add();
                    }
                }}
                onBlur={add}
                placeholder={placeholder || "Type and press Enter or comma…"}
                data-testid={testid}
            />
        </div>
    );
}

// HoursInput — array of {day, from, to}
function HoursInput({ value, onChange, testid }) {
    const list = Array.isArray(value) ? value : [];
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const add = () => onChange([...list, { day: "Mon", from: "09:00", to: "12:00" }]);
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    const patch = (i, k, v) => {
        const next = list.map((it, idx) => (idx === i ? { ...it, [k]: v } : it));
        onChange(next);
    };
    return (
        <div>
            {list.length === 0 ? (
                <div className="text-xs text-[#17212B]/50 mb-2">
                    No slots yet. Add at least one.
                </div>
            ) : null}
            <div className="space-y-2">
                {list.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <select
                            value={it.day}
                            onChange={(e) => patch(i, "day", e.target.value)}
                            className="col-span-3 border border-[#123B5D]/20 rounded-md h-9 px-2 text-sm"
                            data-testid={`${testid}-day-${i}`}
                        >
                            {days.map((d) => (
                                <option key={d}>{d}</option>
                            ))}
                        </select>
                        <Input
                            type="time"
                            value={it.from}
                            onChange={(e) => patch(i, "from", e.target.value)}
                            className="col-span-4"
                        />
                        <Input
                            type="time"
                            value={it.to}
                            onChange={(e) => patch(i, "to", e.target.value)}
                            className="col-span-4"
                        />
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="col-span-1 text-[#DC2626] text-xs"
                        >
                            <X size={14} />
                        </button>
                    </div>
                ))}
            </div>
            <button
                type="button"
                onClick={add}
                className="mt-3 text-sm text-[#0F766E] font-semibold hover:underline"
                data-testid={`${testid}-add`}
            >
                + Add slot
            </button>
        </div>
    );
}

// UploadPicker — single-file upload; on success sets scalar field to returned url
function UploadPicker({ category, value, onChange, testid }) {
    const [busy, setBusy] = useState(false);
    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("category", category);
            const { data } = await api.post("/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };
    return (
        <div className="flex items-center gap-3">
            {value ? (
                <div className="text-xs font-mono text-[#0F766E] max-w-md truncate">
                    {value}
                </div>
            ) : (
                <div className="text-xs text-[#17212B]/50">No file uploaded yet</div>
            )}
            <label className="ddi-outline-btn text-xs inline-flex items-center gap-1 cursor-pointer">
                <Upload size={12} />
                {busy ? "Uploading…" : value ? "Replace" : "Upload"}
                <input type="file" className="hidden" onChange={upload} data-testid={testid} />
            </label>
            {value ? (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="text-[#DC2626] text-xs"
                >
                    Remove
                </button>
            ) : null}
        </div>
    );
}

function getNested(obj, path) {
    return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj || {});
}
function setNested(obj, path, value) {
    const keys = path.split(".");
    const out = { ...(obj || {}) };
    let cur = out;
    for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    return out;
}

export function ScalarStepForm({ step, profile, onPatch, profileExt, onPatchProfileExt, testid = "wizard-scalar" }) {
    const values = profile || {};
    const extValues = profileExt || {};
    const [extDraft, setExtDraft] = useState({});
    const [savingExt, setSavingExt] = useState(false);

    const extSection = step.profileExtSection;
    const extFields = extSection?.fields || [];
    const extCurrent = (name) => (name in extDraft ? extDraft[name] : (extValues[name] ?? ""));
    const setExt = (name) => (v) => setExtDraft((d) => ({ ...d, [name]: v }));

    async function saveExt() {
        if (!extFields.length || !onPatchProfileExt) return;
        const patch = {};
        for (const f of extFields) {
            if (f.name in extDraft) patch[f.name] = extDraft[f.name];
        }
        if (!Object.keys(patch).length) {
            toast.info("Nothing to save yet.");
            return;
        }
        setSavingExt(true);
        try {
            await onPatchProfileExt(patch);
            setExtDraft({});
            toast.success("Optional details saved.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSavingExt(false);
        }
    }

    return (
        <div className="space-y-5">
            {step.fields.map((f) => {
                const val = f.name.includes(".") ? getNested(values, f.name) : values[f.name];
                const set = (v) => {
                    if (f.name.includes(".")) {
                        // patch object at root of nested path
                        const rootKey = f.name.split(".")[0];
                        const updated = setNested({ [rootKey]: values[rootKey] || {} }, f.name, v);
                        onPatch({ [rootKey]: updated[rootKey] });
                    } else {
                        onPatch({ [f.name]: v });
                    }
                };
                const tid = `${testid}-${f.name.replace(/\./g, "-")}`;
                return (
                    <div key={f.name}>
                        <Label>
                            {f.label}
                            {f.required ? <span className="text-[#DC2626]"> *</span> : null}
                        </Label>
                        <div className="mt-1.5">
                            {f.type === "textarea" || f.type === "longtext" ? (
                                <Textarea
                                    rows={f.rows || 4}
                                    value={val || ""}
                                    onChange={(e) => set(e.target.value)}
                                    placeholder={f.placeholder}
                                    data-testid={tid}
                                />
                            ) : f.type === "number" ? (
                                <Input
                                    type="number"
                                    min={f.min}
                                    max={f.max}
                                    value={val ?? ""}
                                    onChange={(e) => set(e.target.value === "" ? "" : Number(e.target.value))}
                                    data-testid={tid}
                                />
                            ) : f.type === "date" ? (
                                <Input
                                    type="date"
                                    value={val || ""}
                                    onChange={(e) => set(e.target.value)}
                                    data-testid={tid}
                                />
                            ) : f.type === "select" ? (
                                <Select value={val || ""} onValueChange={(v) => set(v)}>
                                    <SelectTrigger data-testid={tid}>
                                        <SelectValue placeholder="Select…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(f.options || []).map((o) => (
                                            <SelectItem key={o} value={o}>
                                                {o}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : f.type === "chips" ? (
                                <ChipsInput
                                    value={val || []}
                                    onChange={set}
                                    placeholder={f.placeholder}
                                    testid={tid}
                                />
                            ) : f.type === "hours" ? (
                                <HoursInput value={val || []} onChange={set} testid={tid} />
                            ) : f.type === "upload" ? (
                                <UploadPicker
                                    category={f.category}
                                    value={val || ""}
                                    onChange={set}
                                    testid={tid}
                                />
                            ) : (
                                <Input
                                    value={val || ""}
                                    onChange={(e) => set(e.target.value)}
                                    placeholder={f.placeholder}
                                    data-testid={tid}
                                />
                            )}
                        </div>
                    </div>
                );
            })}

            {extSection && extFields.length > 0 ? (
                <div
                    className="mt-6 pt-6 border-t border-[#123B5D]/10"
                    data-testid={`${testid}-profile-ext-section`}
                >
                    <div className="mb-3">
                        <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Optional · appears on public profile</div>
                        <h3 className="text-base font-semibold text-[#123B5D]">{extSection.title}</h3>
                        {extSection.description ? (
                            <p className="text-xs text-[#17212B]/60 mt-1">{extSection.description}</p>
                        ) : null}
                    </div>
                    <div className="space-y-4">
                        {extFields.map((f) => {
                            const tid = `${testid}-ext-${f.name}`;
                            const v = extCurrent(f.name);
                            return (
                                <div key={f.name}>
                                    <Label>{f.label}</Label>
                                    <div className="mt-1.5">
                                        {f.type === "textarea" ? (
                                            <Textarea
                                                rows={f.rows || 3}
                                                value={v || ""}
                                                onChange={(e) => setExt(f.name)(e.target.value)}
                                                placeholder={f.placeholder}
                                                maxLength={f.maxLength}
                                                data-testid={tid}
                                            />
                                        ) : (
                                            <Input
                                                type={f.type === "url" ? "url" : "text"}
                                                value={v || ""}
                                                onChange={(e) => setExt(f.name)(e.target.value)}
                                                placeholder={f.placeholder}
                                                maxLength={f.maxLength}
                                                data-testid={tid}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <button
                            type="button"
                            onClick={saveExt}
                            disabled={savingExt}
                            className="ddi-btn-secondary text-sm"
                            data-testid={`${testid}-save-profile-ext`}
                        >
                            {savingExt ? "Saving…" : "Save optional details"}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function ListStepForm({ step, items, refresh, testid = "wizard-list" }) {
    const [draft, setDraft] = useState({});
    const [editingId, setEditingId] = useState(null);
    const list = items || [];

    const save = async () => {
        // Validate required fields
        for (const f of step.itemFields) {
            if (f.required && !String(draft[f.name] || "").trim()) {
                toast.error(`${f.label} is required`);
                return;
            }
        }
        try {
            if (editingId) {
                await api.put(`/onboarding/items/${editingId}`, { payload: draft });
            } else {
                await api.post("/onboarding/items", { kind: step.listKind, payload: draft });
            }
            setDraft({});
            setEditingId(null);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };
    const edit = (it) => {
        setDraft(it.payload || {});
        setEditingId(it.id);
    };
    const remove = async (id) => {
        try {
            await api.delete(`/onboarding/items/${id}`);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };
    return (
        <div className="space-y-6">
            <div className="ddi-card p-4">
                <div className="text-sm font-semibold text-[#123B5D] mb-3">
                    {editingId ? "Edit entry" : "Add new"}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {step.itemFields.map((f) => (
                        <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                            <Label>
                                {f.label}
                                {f.required ? <span className="text-[#DC2626]"> *</span> : null}
                            </Label>
                            <div className="mt-1.5">
                                {f.type === "textarea" ? (
                                    <Textarea
                                        rows={3}
                                        value={draft[f.name] || ""}
                                        onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
                                        data-testid={`${testid}-${step.listKind}-${f.name}`}
                                    />
                                ) : f.type === "select" ? (
                                    <Select
                                        value={draft[f.name] || ""}
                                        onValueChange={(v) => setDraft({ ...draft, [f.name]: v })}
                                    >
                                        <SelectTrigger data-testid={`${testid}-${step.listKind}-${f.name}`}>
                                            <SelectValue placeholder="Select…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(f.options || []).map((o) => (
                                                <SelectItem key={o} value={o}>
                                                    {o}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        type={f.type === "number" ? "number" : "text"}
                                        min={f.min}
                                        max={f.max}
                                        value={draft[f.name] ?? ""}
                                        onChange={(e) => setDraft({ ...draft, [f.name]: f.type === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value })}
                                        data-testid={`${testid}-${step.listKind}-${f.name}`}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={save}
                        className="ddi-teal-btn text-sm"
                        data-testid={`${testid}-${step.listKind}-save`}
                    >
                        {editingId ? "Save changes" : "Add entry"}
                    </button>
                    {editingId ? (
                        <button
                            type="button"
                            className="text-sm text-[#17212B]/60"
                            onClick={() => {
                                setDraft({});
                                setEditingId(null);
                            }}
                        >
                            Cancel
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="space-y-2">
                {list.length === 0 ? (
                    <div className="text-sm text-[#17212B]/50">No entries yet.</div>
                ) : (
                    list.map((it) => (
                        <div
                            key={it.id}
                            className="ddi-card p-4 flex items-start justify-between"
                            data-testid={`${testid}-${step.listKind}-row-${it.id}`}
                        >
                            <div>
                                <div className="text-sm font-semibold text-[#123B5D]">
                                    {step.summary ? step.summary(it.payload || {}) : "Entry"}
                                </div>
                                {Object.keys(it.payload || {}).length > 0 ? (
                                    <div className="text-xs text-[#17212B]/60 mt-1">
                                        {Object.entries(it.payload)
                                            .filter(([, v]) => v)
                                            .map(([k, v]) => `${k}: ${v}`)
                                            .join(" · ")
                                            .slice(0, 200)}
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                                <button
                                    type="button"
                                    onClick={() => edit(it)}
                                    className="text-[#0F766E] font-semibold"
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(it.id)}
                                    className="text-[#DC2626] font-semibold"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export function UploadsStepForm({ step, uploads, profile, onPatch, refresh, testid = "wizard-uploads" }) {
    const byCat = {};
    for (const u of uploads || []) {
        byCat[u.category] = byCat[u.category] || [];
        byCat[u.category].push(u);
    }
    const uploadFor = async (cat, file, profileField) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", cat.key);
        try {
            const { data } = await api.post("/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (profileField) {
                await onPatch({ [profileField]: data.url });
            }
            toast.success(`Uploaded ${cat.label}`);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        }
    };
    const remove = async (u) => {
        try {
            await api.delete(`/me/uploads/${u.id}`);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };
    return (
        <div className="space-y-5">
            {step.categories.map((cat) => (
                <div key={cat.key} className="ddi-card p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-[#123B5D]">
                                {cat.label}
                            </div>
                            <div className="text-xs text-[#17212B]/60">
                                {cat.kind === "image"
                                    ? "JPG / PNG / WEBP · ≤5MB"
                                    : cat.kind === "video"
                                    ? "MP4 / WEBM · ≤50MB"
                                    : "PDF · ≤10MB"}
                            </div>
                        </div>
                        <label className="ddi-outline-btn text-xs inline-flex items-center gap-1 cursor-pointer">
                            <Upload size={12} /> Upload
                            <input
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) uploadFor(cat, f, cat.profileField);
                                    e.target.value = "";
                                }}
                                data-testid={`${testid}-${cat.key}`}
                            />
                        </label>
                    </div>
                    <ul className="mt-3 space-y-1">
                        {(byCat[cat.key] || []).map((u) => (
                            <li key={u.id} className="text-xs text-[#17212B]/80 flex items-center justify-between">
                                <span className="font-mono truncate max-w-md">{u.url}</span>
                                <button
                                    type="button"
                                    className="text-[#DC2626]"
                                    onClick={() => remove(u)}
                                >
                                    Delete
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}
